import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PartnerProvider, usePartner } from './contexts/PartnerContext';
import { StreamerModeProvider } from './contexts/StreamerModeContext';
import Header from './components/Header';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Footer from './components/Footer';
import Dashboard from './components/Dashboard';
import Payments from './components/Payments';
import PromoCodes from './components/PromoCodes';
import PaymentsAccessGuard from './components/PaymentsAccessGuard';
import Login from './components/Login';
import LogoutModal from './components/LogoutModal';
import PartnerModal from './components/PartnerModal';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isPartnerApproved, loading } = usePartner();

  if (loading) {
    return (
      <div className="min-h-screen bg-tesla-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPartnerApproved) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isPartnerApproved, loading } = usePartner();

  if (loading) {
    return (
      <div className="min-h-screen bg-tesla-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tesla-red"></div>
      </div>
    );
  }

  if (isPartnerApproved) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const GlobalModals: React.FC = () => {
  const navigate = useNavigate();
  const { 
    showLogoutModal, 
    showPartnerModal,
    logout, 
    hideLogoutConfirmation,
    hidePartnerModalConfirmation 
  } = usePartner();

  const handleConfirmLogout = () => {
    hideLogoutConfirmation();
    logout();
    navigate('/');
  };

  const handleCancelLogout = () => {
    hideLogoutConfirmation();
  };

  return (
    <>
      <LogoutModal
        isOpen={showLogoutModal}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />
      <PartnerModal
        isOpen={showPartnerModal}
        onClose={hidePartnerModalConfirmation}
      />
    </>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Публичные маршруты */}
      <Route path="/" element={
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Header />
          <Hero />
          <HowItWorks />
          <Footer />
        </motion.div>
      } />
      
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Защищенные маршруты */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Header />
            <Dashboard />
          </motion.div>
        </ProtectedRoute>
      } />

      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentsAccessGuard>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.25, 0.25, 0, 1] }}
            >
              <Header />
              <Payments />
            </motion.div>
          </PaymentsAccessGuard>
        </ProtectedRoute>
      } />

      <Route path="/promocodes" element={
        <ProtectedRoute>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.25, 0.25, 0, 1] }}
          >
            <Header />
            <PromoCodes />
          </motion.div>
        </ProtectedRoute>
      } />

      {/* Редирект на главную для неизвестных маршрутов */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <StreamerModeProvider>
        <PartnerProvider>
          <Router>
            <div className="App min-h-screen bg-tesla-black">
              <AppRoutes />
              <GlobalModals />
            </div>
          </Router>
        </PartnerProvider>
      </StreamerModeProvider>
    </ErrorBoundary>
  );
}

export default App;
